import { defineStore } from 'pinia'
import api from '@/plugins/axios' // Your configured axios instance
import {
  getAuth,
  onAuthStateChanged,
  signOut,
  getIdToken
  // Import other Firebase methods like createUserWithEmailAndPassword where needed (usually in components)
} from 'firebase/auth'
import { getFirestore, doc, onSnapshot } from 'firebase/firestore'

export const useUserStore = defineStore('user', {
  state: () => ({
    // --- State Variables ---
    user: null, // Holds profile data from YOUR backend DB
    firebaseUid: null, // Store Firebase UID separately if needed
    token: null, // Current Firebase ID Token
    isAuthenticated: false, // Primarily derived from user/token presence after check
    isLoading: false, // General loading state for auth operations
    authIsReady: false, // Flag: True after the initial onAuthStateChanged check completes
    error: null, // Store auth/fetch errors
    // New state for billing credits subscription
    billingCreditsLeft: null,
    billingUnsubscribe: null
  }),

  getters: {
    // You can add getters if needed, e.g., derived state
    // getUserProfile: (state) => state.user,
    // getIsAuthenticated: (state) => state.isAuthenticated,
    // getAuthIsReady: (state) => state.authIsReady,
  },

  actions: {
    // --- Core Backend Interaction (Called by Listener or Registration) ---
    async authenticateUserWithBackend (token) {
      if (!token) {
        console.warn('authenticateUserWithBackend called without token.')
        this.clearUserInternal() // Use internal clearer
        return
      }
      this.isLoading = true
      this.error = null
      try {
        console.log('Attempting to fetch user profile from backend...')
        const response = await api.get('/user', {
          headers: { Authorization: `Bearer ${token}` }
        })

        if (response.data) {
          console.log('Backend profile data received:', response.data)
          this.user = response.data // Store backend profile
          // Assuming UID is part of response.data or can be derived if needed
          // this.firebaseUid = response.data.uid || this.firebaseUid; // Ensure UID consistency if possible
          this.isAuthenticated = true // Backend confirmation
          this.token = token // Store the current valid token
        } else {
          // Handle cases where backend returns 200 OK but no data (if possible)
          console.warn('Backend returned success but no user data.')
          throw new Error('No user data received from backend /user endpoint.')
        }
      } catch (error) {
        console.error('Error authenticating/fetching user from backend:', error)
        this.error = error.response?.data?.message || error.message || 'Failed to fetch user profile.'
        // Backend fetch failed, clear local backend profile data but keep Firebase auth state
        this.user = null
        // Decide if failure to get *backend* data means !isAuthenticated.
        // Let's say yes for now, requires backend profile to be truly "authenticated" in the app context.
        this.isAuthenticated = false
        // Don't clear token/firebaseUid here, they might still be valid from Firebase perspective
      } finally {
        this.isLoading = false
      }
    },

    // --- User Registration in Backend (Pattern A - Explicit Call) ---
    // Call this from your Register component AFTER Firebase user creation succeeds.
    async registerUserInBackend (firebaseUser, idToken, additionalData = {}) {
      if (!firebaseUser || !idToken) {
        console.error('registerUserInBackend requires firebaseUser and idToken')
        throw new Error('Missing required arguments for backend registration.')
      }
      this.isLoading = true
      this.error = null
      console.log('Registering user in backend DB:', firebaseUser.uid, additionalData.userName)
      try {
        const response = await api.post('/user', { // Your backend user creation endpoint
          user_id: firebaseUser.uid,
          user_email: firebaseUser.email,
          user_name: additionalData.userName,
          user_lang: additionalData.locale
        }, {
          headers: { Authorization: `Bearer ${idToken}` }
        })

        // Assume backend returns the newly created user profile on success
        if (response.data) {
          console.log('Backend registration successful, profile:', response.data)
          this.user = response.data // Store the profile returned by backend
          this.firebaseUid = firebaseUser.uid // Store UID
          this.token = idToken // Store token used for registration
          this.isAuthenticated = true // Mark as authenticated in app context
        } else {
          throw new Error('Backend did not return user data after registration.')
        }
      } catch (error) {
        console.error('Error registering user in backend:', error)
        this.error = error.response?.data?.message || error.message || 'Backend registration failed.'
        this.clearUserInternal() // Clear state on critical backend registration failure
        this.isAuthenticated = false
        // Optional: Sign out from Firebase if backend fails? Controversial.
        // const auth = getAuth(); await signOut(auth);
        throw error // Re-throw so the calling component knows it failed
      } finally {
        this.isLoading = false
      }
    },

    // --- Logout ---
    async logout () {
      console.log('Logout action called.')
      this.isLoading = true
      try {
        const auth = getAuth()
        await signOut(auth)
        console.log('Firebase signOut successful.')
        // The onAuthStateChanged listener will handle clearing state via clearUserInternal.
      } catch (error) {
        console.error('Error signing out from Firebase:', error)
        this.error = error.message || 'Logout failed.'
        // Force clear local state even if Firebase signout fails
        this.clearUserInternal()
      } finally {
        // Listener should set loading false, but set it here just in case listener has issues
        this.isLoading = false
      }
    },

    // --- Internal State Helpers (mostly called by listener) ---
    _updateFirebaseAuthState (fbUser, fbToken) {
      // Updates Firebase-related state, called when listener detects user
      this.firebaseUid = fbUser.uid
      this.token = fbToken
      // Tentatively true, authenticateUserWithBackend confirms based on YOUR DB
      this.isAuthenticated = true
      this.error = null // Clear previous errors on new auth state
    },

    clearUserInternal () {
      // Clears all user-related state, called by listener on sign-out or error
      console.log('Clearing internal user state.')
      this.user = null
      this.token = null
      this.firebaseUid = null
      this.isAuthenticated = false
      this.error = null
      this.unsubscribeBillingCredits() // Stop listening to billing credits
      // Don't reset isLoading here, let the calling process manage it
      // Don't reset authIsReady here
    },

    _setAuthIsReady () {
      // Marks the initial auth check as complete
      if (!this.authIsReady) {
        console.log('Authentication state is ready.')
        this.authIsReady = true
      }
    },

    // --- Other Actions (Keep if needed) ---
    async fetchUserStats () {
      if (!this.isAuthenticated || !this.token) { // Check app authentication state
        console.warn('Cannot fetch stats, user not authenticated in app context.')
        return null
      }
      // Add loading/error handling specific to this action if desired
      try {
        const response = await api.get('/user/stats', {
          headers: { Authorization: `Bearer ${this.token}` }
        })
        if (this.user) { // Ensure user profile object exists
          this.user.stats = response.data
        } else {
          console.warn('User profile object not available to attach stats.')
        }
      } catch (error) {
        console.error('Error fetching user stats:', error)
        this.error = error.response?.data?.message || error.message || 'Failed to fetch stats.'
        // Decide if this error should clear auth state - probably not.
      }
    },
    // New actions for billing subscription
    subscribeToBillingCredits () {
      if (this.billingUnsubscribe) {
        this.billingUnsubscribe()
      }
      const db = getFirestore()
      const billingDoc = doc(db, 'users', this.firebaseUid)
      this.billingUnsubscribe = onSnapshot(billingDoc, (snapshot) => {
        if (snapshot.exists()) {
          this.billingCreditsLeft = snapshot.data().billing.credits_left
        } else {
          this.billingCreditsLeft = null
        }
      }, (error) => {
        console.error('Error in billing credits subscription:', error)
        this.billingCreditsLeft = null
      })
    },
    unsubscribeBillingCredits () {
      if (this.billingUnsubscribe) {
        this.billingUnsubscribe()
        this.billingUnsubscribe = null
        this.billingCreditsLeft = null
      }
    }
    // --- REMOVE Obsolete Actions ---
    // authenticateUser: Renamed to authenticateUserWithBackend for clarity
    // fetchUser: Removed (replaced by persistent listener)
    // resolveUser: Removed (replaced by persistent listener and authIsReady flag)
    // registerUser: Renamed to registerUserInBackend for clarity
    // setUser: Removed (state set directly or via internal helpers)
    // clearUser: Renamed to clearUserInternal to avoid conflict if exported

  } // End actions
})

// --- Persistent Listener Setup ---
// Export this function to be called ONCE from main.js
export function initializeAuthListener () {
  console.log('Initializing Firebase Auth listener...')
  const store = useUserStore()
  const auth = getAuth()

  // Use a flag to prevent execution if already initialized (safety measure)
  if (window.authListenerInitialized) {
    console.warn('Auth listener already initialized.')
    return
  }
  window.authListenerInitialized = true // Set global flag

  onAuthStateChanged(auth, async (user) => {
    console.log('onAuthStateChanged event received.')
    // DO NOT set isLoading here - let actions manage their own loading state

    if (user) {
      console.log('Firebase user detected:', user.uid)
      try {
        const token = await getIdToken(user, /* forceRefresh */ true)
        store._updateFirebaseAuthState(user, token)
        await store.authenticateUserWithBackend(token)
        store.subscribeToBillingCredits()
      } catch (error) {
        // This catches errors getting token or critical errors in _updateFirebaseAuthState
        console.error('Error during Firebase user processing:', error)
        store.clearUserInternal() // Critical error, clear everything
      }
    } else {
      console.log('Firebase user signed out.')
      store.clearUserInternal()
    }

    // Mark auth as ready *after* processing the first auth state event
    store._setAuthIsReady()
  }, (error) => {
    // Listener Error (rare, e.g., network issues initializing listener)
    console.error('CRITICAL: Error in onAuthStateChanged listener:', error)
    store.error = 'Authentication listener failed.'
    store.clearUserInternal()
    store._setAuthIsReady() // Mark ready even on error to unblock app, but show error
  })
}
